import React, { Fragment, useContext, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useCommonDetailsContext } from "../context/Common/common";
import { useMemo } from "react";
import NavBar from "../components/layout/NavBar";
import Breadcrumb from "../components/layout/Breadcrumb";
import Loader from "../components/common/Loader";
// import { theme } from "../theme/theme";
import { createAppTheme } from "../theme/theme";
import AuthContext from "../context/auth/AuthContext";
import FilingMasterContext from "../context/filing-master/FilingMasterContext";
import { useNavigate, useLocation } from "react-router-dom";
import CustomerContext from "../context/customer/CustomerContext";

type Props = {
  children?: React.ReactNode;
};

export const PageLayout: React.FC<Props> = ({ children }) => {
  const customerContext = useContext(CustomerContext);
  const { selectedCustomer, customers } = customerContext || {};
  const {
    isColorChange,
    publishedBranding,
    clientScopedBranding,
    setClientScopedBranding,
    isBrandingLoaded,
    isClientBrandingLoading,
    validateParentData,
    fetchClientBranding,
  } = useCommonDetailsContext();
  const authContext = useContext(AuthContext);
  const { isPortalAuthenticated, isAuthenticated } = authContext;
  const isUserAuthenticated = isPortalAuthenticated && isAuthenticated;
  const filingMasterContext = useContext(FilingMasterContext);
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const contextSelected: any = (filingMasterContext as any)?.selectedFilingMaster;
  const storageSelected: any = (() => {
    try {
      const raw = localStorage.getItem("selectedFilingMaster");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const selectedFilingMaster: any = contextSelected ?? storageSelected;

  const isClientFilingRoute =
    (path === "/filing-master" || path === "/postaward-master-form") &&
    (selectedFilingMaster?.from === "Add client filing" || selectedFilingMaster?.from === "Edit client filing");

  const isClientScopedRoute = [
    "/client-filing-tracker-list",
    "/calendar",
    "/add-filings",
    "/add-filing-postaward",
    "/branding-configuration",
  ].some((prefix) => path.startsWith(prefix)) || isClientFilingRoute;

  useEffect(() => {
    if (!isClientScopedRoute) {
      setClientScopedBranding(null);
    } else if (isClientFilingRoute && !validateParentData?.isSingleParent && fetchClientBranding) {
      const targetId = selectedFilingMaster?.customerId || selectedCustomer?.customerId;
      if (targetId && (!clientScopedBranding || String(clientScopedBranding.customerId) !== String(targetId))) {
        const customerList = customers || [];
        const selected: any = customerList.find((c: any) => String(c.customerId) === String(targetId)) || (String(selectedCustomer?.customerId) === String(targetId) ? selectedCustomer : null);
        const customerType = selected?.customerType || selected?.clientType;
        fetchClientBranding(targetId, customerType);
      }
    }
  }, [path, isClientScopedRoute, isClientFilingRoute, validateParentData?.isSingleParent, fetchClientBranding, setClientScopedBranding, selectedFilingMaster?.customerId, selectedCustomer, customers, clientScopedBranding]);

  const activeBranding = useMemo(() => {
    if (validateParentData?.isSingleParent) {
      return publishedBranding;
    }
    if (isClientScopedRoute) {
      return clientScopedBranding || publishedBranding;
    }
    return publishedBranding;
  }, [validateParentData?.isSingleParent, isClientScopedRoute, clientScopedBranding, publishedBranding]);

  const theme = useMemo(
    () => createAppTheme(isColorChange, activeBranding),
    [isColorChange, activeBranding]
  );

  // console.log("PageLayout selectedCustomer", selectedCustomer);
  const getPathClientId = (basePath: string) => {
    const routePrefix = basePath.replace("/", "");
    const pathParts = path.split("/").filter(Boolean);

    if (pathParts[0] !== routePrefix || pathParts.length < 2) {
      return null;
    }

    return pathParts[1];
  };

  const getClientName = (clientId: string | null) => {
    if (!clientId) return null;
    // Try to get client name from location state
    const state = location.state as any;
    return state?.clientName || `Client ${clientId}`;
  };

  const getBreadcrumbsAndTitle = () => {
    // Home page
    if (path === '/' || path === '/client-profiles') {
      return { title: 'CLIENT PROFILE LIST', breadcrumbs: null };
    }

    // Customer/Client pages
    if (path === '/customer') {
      console.log("selectedCustomer", selectedCustomer);
      const label = selectedCustomer ? 'Edit Client' : 'Add New Client';

      return {
        title: selectedCustomer ? 'EDIT CLIENT' : 'ADD NEW CLIENT',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: label, path: '/customer' }]
      };
    }

    // Filing Master pages
    if (path === '/filing-master-list') {
      return {
        title: 'FILING MASTER LIST',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Filing Master List', path: '/filing-master-list' }]
      };
    }
    if (path === '/filing-master') {
      const contextSelected: any = (filingMasterContext as any)?.selectedFilingMaster;
      const storageSelected: any = (() => {
        try {
          const raw = localStorage.getItem("selectedFilingMaster");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();
      const selectedFilingMaster: any = contextSelected ?? storageSelected;
      const state = location.state as any;

      const isClientFilingEdit = selectedFilingMaster?.from === "Edit client filing";
      const isClientFilingAdd = selectedFilingMaster?.from === "Add client filing";
      const isMasterFilingEdit = Boolean(
        selectedFilingMaster &&
        selectedFilingMaster.filingName &&
        !selectedFilingMaster.from &&
        selectedFilingMaster.recordType !== "PostAward"
      );

      const parentFrom = state?.from;
      const calendarClientId = getPathClientId("/calendar");
      console.log("BC parentFrom", parentFrom, "calendarClientId", calendarClientId);

      let parentLabel = 'Filing Master List';
      let parentPath = '/filing-master-list';
      if (parentFrom === '/filing-master-approval-list') {
        parentLabel = 'Filing Master Approval List';
        parentPath = '/filing-master-approval-list';
      } else if (parentFrom === '/client-filing-tracker-list' || parentFrom === '/calendar') {
        parentLabel = 'Client Filing Tracker List';
        parentPath = '/client-filing-tracker-list';
      } else if (parentFrom === '/filing-master-list') {
        parentLabel = 'Filing Master List';
        parentPath = '/filing-master-list';
      }

      const title = isClientFilingEdit
        ? "EDIT CLIENT FILING"
        : isClientFilingAdd
          ? "ADD CLIENT FILING"
          : isMasterFilingEdit
            ? "EDIT FILING MASTER RECORD"
            : "ADD FILING MASTER RECORD";

      const lastCrumbLabel = isClientFilingEdit
        ? "Edit Client Filing"
        : isClientFilingAdd
          ? "Add Client Filing"
          : isMasterFilingEdit
            ? "Edit Filing Master Record"
            : "Add Filing Master Record";

      return {
        title,
        breadcrumbs: [
          { label: 'Home', path: '/' },
          { label: parentLabel, path: parentPath },
          { label: lastCrumbLabel, path: '/filing-master' },
        ]
      };
    }
    if (path === '/filing-master-approval-list') {
      return {
        title: 'FILING MASTER APPROVAL LIST',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Filing Master Approval List', path: '/filing-master-approval-list' }]
      };
    }

    // Post Award pages
    if (path === '/postaward-filing-master-list') {
      return {
        title: 'POST AWARD MASTER LIST',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Post Award Master List', path: '/postaward-filing-master-list' }]
      };
    }
    if (path === '/postaward-master-form') {
      const contextSelected: any = (filingMasterContext as any)?.selectedFilingMaster;
      const storageSelected: any = (() => {
        try {
          const raw = localStorage.getItem("selectedFilingMaster");
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      })();

      const selectedFilingMaster: any = contextSelected ?? storageSelected;
      const state = location.state as any;

      const isClientFilingEdit = selectedFilingMaster?.from === "Edit client filing";
      const isClientFilingAdd = selectedFilingMaster?.from === "Add client filing";
      const isPostAwardMasterEdit = Boolean(
        selectedFilingMaster &&
        selectedFilingMaster.filingName &&
        !selectedFilingMaster.from &&
        selectedFilingMaster.recordType === "PostAward"
      );

      const parentFrom = state?.from;

      let parentLabel = 'Post Award Master List';
      let parentPath = '/postaward-filing-master-list';
      if (parentFrom === '/postaward-approval-list') {
        parentLabel = 'Post Award Approval List';
        parentPath = '/postaward-approval-list';
      } else if (parentFrom === '/client-filing-tracker-list' || parentFrom === '/calendar') {
        parentLabel = 'Client Filing Tracker List';
        parentPath = '/client-filing-tracker-list';
      } else if (parentFrom === '/postaward-filing-master-list') {
        parentLabel = 'Post Award Master List';
        parentPath = '/postaward-filing-master-list';
      }

      const title = isClientFilingEdit
        ? "EDIT CLIENT FILING"
        : isClientFilingAdd
          ? "ADD CLIENT FILING"
          : isPostAwardMasterEdit
            ? "EDIT POST AWARD MASTER RECORD"
            : "ADD POST AWARD MASTER RECORD";

      const lastCrumbLabel = isClientFilingEdit
        ? "Edit Client Filing"
        : isClientFilingAdd
          ? "Add Client Filing"
          : isPostAwardMasterEdit
            ? "Edit Post Award Master Record"
            : "Add Post Award Master Record";
      return {
        title,
        breadcrumbs: [
          { label: 'Home', path: '/' },
          { label: parentLabel, path: parentPath },
          { label: lastCrumbLabel, path: '/postaward-master-form' },
        ]
      };
    }
    if (path === '/postaward-approval-list') {
      return {
        title: 'POST AWARD APPROVAL LIST',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Post Award Approval List', path: '/postaward-approval-list' }]
      };
    }

    // Client Filing Master pages
    if (path === '/client-filing-master-approval-list') {
      return {
        title: 'CLIENT FILING MASTER APPROVAL LIST',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Client Filing Master Approval List', path: '/client-filing-master-approval-list' }]
      };
    }
    if (path === '/PostAwardClientfilingMasterApprovalList') {
      return {
        title: 'POST AWARD CLIENT FILING MASTER APPROVAL LIST',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Post Award Client Filing Master Approval List', path: '/PostAwardClientfilingMasterApprovalList' }]
      };
    }

    // Client-specific pages with clientId
    const clientFilingMasterClientId = getPathClientId("/client-filing-master-list");
    if (path === "/client-filing-master-list" || !!clientFilingMasterClientId) {
      const clientName = getClientName(clientFilingMasterClientId);
      const breadcrumbs = [{ label: 'Home', path: '/' }];
      if (clientFilingMasterClientId && clientName) {
        breadcrumbs.push({ label: clientName, path: `/client-filing-master-list/${clientFilingMasterClientId}` });
      }
      return { title: 'CLIENT FILING MASTER LIST', breadcrumbs };
    }
    const clientFilingTrackerClientId = getPathClientId("/client-filing-tracker-list");
    if (path === "/client-filing-tracker-list" || !!clientFilingTrackerClientId) {
      let clientName = null;

      // 🔥 Priority 1: selectedCustomer (best source)
      if (selectedCustomer && selectedCustomer.customerName) {
        clientName = selectedCustomer.customerName;
      }
      // 🔥 Priority 2: route param
      else if (clientFilingTrackerClientId) {
        clientName = getClientName(clientFilingTrackerClientId);
      }
      const breadcrumbs = [
        { label: 'Home', path: '/' },
        { label: 'Client Filing Tracker List', path: '/client-filing-tracker-list' },
      ];

      if (clientName) {
        breadcrumbs.push({
          label: clientName,
          path: `/client-filing-tracker-list/${clientFilingTrackerClientId || selectedCustomer?.customerId}`
        });
      }
      return { title: 'CLIENT FILING TRACKER LIST', breadcrumbs };
    }
    const calendarClientId = getPathClientId("/calendar");
    if (path === "/calendar" || !!calendarClientId) {
      const clientName = getClientName(calendarClientId);

      const breadcrumbs = [
        { label: "Home", path: "/" },
        {
          label: "Client Filing Tracker List",
          path: "/client-filing-tracker-list",
        },
      ];

      if (calendarClientId && clientName) {
        breadcrumbs.push({
          label: clientName,
          path: `/client-filing-tracker-list/${calendarClientId}`,
        });
      }

      return { title: "CALENDAR VIEW", breadcrumbs };
    }
    const deferredClientId = getPathClientId("/add-filings");
    if (path === "/add-filings" || !!deferredClientId) {
      const clientName = getClientName(deferredClientId);
      const breadcrumbs = [
        { label: "Home", path: "/" },
        { label: "TrackNow Deferred Filing List", path: "/add-filings" },
      ];
      if (deferredClientId && clientName) {
        breadcrumbs.push({ label: clientName, path: `/add-filings/${deferredClientId}` });
      }
      return { title: "TRACKNOW DEFERRED FILING LIST", breadcrumbs };
    }
    const postAwardDeferredClientId = getPathClientId("/add-filing-postaward");
    if (path === "/add-filing-postaward" || !!postAwardDeferredClientId) {
      const clientName = getClientName(postAwardDeferredClientId);
      const breadcrumbs = [
        { label: "Home", path: "/" },
        { label: "Post Award Deferred Filing List", path: "/add-filing-postaward" },
      ];
      if (postAwardDeferredClientId && clientName) {
        breadcrumbs.push({ label: clientName, path: `/add-filing-postaward/${postAwardDeferredClientId}` });
      }
      return { title: "POST AWARD DEFERRED FILING LIST", breadcrumbs };
    }

    // Configuration pages
    if (path === '/business-category') {
      return {
        title: 'BUSINESS CATEGORY',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Business Category', path: '/business-category' }]
      };
    }
    if (path === '/ProgramNames') {
      return {
        title: 'PROGRAM NAMES',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Program Names', path: '/ProgramNames' }]
      };
    }
    if (path === '/ApprovalConfig') {
      return {
        title: 'APPROVAL CONFIGURATION',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Approval Configuration', path: '/ApprovalConfig' }]
      };
    }
    if (path === '/PostAwardApproverConfiguration') {
      return {
        title: 'POST AWARD APPROVER CONFIGURATION',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Post Award Approver Configuration', path: '/PostAwardApproverConfiguration' }]
      };
    }
    if (path === '/reminder') {
      return {
        title: 'REMINDER CONFIGURATION',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Reminder Configuration', path: '/reminder' }]
      };
    }
    if (path === '/notificationConfiguration') {
      return {
        title: 'NOTIFICATION CONFIGURATION',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Notification Configuration', path: '/notificationConfiguration' }]
      };
    }
    if (path === '/branding-configuration') {
      return {
        title: 'BRANDING CONFIGURATION',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Branding Configuration', path: '/branding-configuration' }]
      };
    }
    if (path === '/user-role-setup') {
      return {
        title: 'USER ROLE SETUP',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'User Role Setup', path: '/user-role-setup' }]
      };
    }
    if (path === '/rollOverFilings') {
      return {
        title: 'ROLL OVER FILINGS',
        breadcrumbs: [{ label: 'Home', path: '/' }, { label: 'Roll Over Filings', path: '/rollOverFilings' }]
      };
    }

    return { title: 'TRACKNOW', breadcrumbs: null };
  };

  const { title, breadcrumbs } = getBreadcrumbsAndTitle();
  const isHomePage = location.pathname === '/' || location.pathname === '/client-profiles';
  const breadcrumbBackAction = (() => {
    if (path.startsWith("/add-filings/")) {
      return () => navigate("/add-filings");
    }
    if (path.startsWith("/add-filing-postaward/")) {
      return () => navigate("/add-filing-postaward");
    }
    if (isHomePage) {
      return null;
    }
    return () => navigate(-1);
  })();
  useEffect(() => {
    console.log("Breadcrum selectedFilingMaster", selectedFilingMaster);

  }, [selectedFilingMaster])
  return (
    <Fragment>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: "100vh",
            // backgroundImage: isUserAuthenticated
            //   ? 'url(/HeaderBackground.png)'
            //   : 'none',
            // backgroundSize: 'cover',
            // backgroundPosition: 'center',
            // backgroundRepeat: 'no-repeat',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <NavBar />
          {isPortalAuthenticated && (!isBrandingLoaded || (isClientScopedRoute && isClientBrandingLoading)) ? (
            <Loader />
          ) : (
            <>
              {isUserAuthenticated && !isHomePage && (
                <Breadcrumb pageTitle={title} onBack={breadcrumbBackAction} breadcrumbOverride={breadcrumbs} />
              )}
              <Grid
                sx={{
                  minHeight: "100vh",
                  paddingTop: 0,
                  width: "100%",
                }}
              >
                {children}
              </Grid>
            </>
          )}
        </Box>
      </ThemeProvider>
    </Fragment >
  );
};
